import type { ComponentChildren } from 'preact'
import { useTranslation } from 'react-i18next'
import { AlertCircle, AlertTriangle, CheckCircle, Info, X } from 'lucide-preact'

type AlertVariant = 'error' | 'warning' | 'success' | 'info'

interface AlertProps {
  variant?: AlertVariant
  message?: string | ComponentChildren
  className?: string
  onDismiss?: () => void
  /** Optional translation key for i18n support */
  translationKey?: string
  /** Optional accessibility label for dismiss button */
  dismissLabel?: string
}

export function Alert({
  variant = 'info',
  message,
  className = '',
  onDismiss,
  translationKey,
  dismissLabel,
}: AlertProps) {
  const { t } = useTranslation()
  const displayMessage = translationKey
    ? t(translationKey)
    : typeof message === 'string'
      ? message
      : undefined

  const variantClasses = {
    error: {
      bg: 'bg-red-50 border-red-200',
      text: 'text-red-600',
      icon: <AlertTriangle className='w-5 h-5 text-red-600' />,
    },
    warning: {
      bg: 'bg-cencert-seal-light border-cencert-seal',
      text: 'text-cencert-navy',
      icon: <AlertCircle className='w-5 h-5 text-cencert-navy' />,
    },
    success: {
      bg: 'bg-cencert-mobile-signature-light border-cencert-mobile-signature',
      text: 'text-cencert-navy',
      icon: <CheckCircle className='w-5 h-5 text-cencert-mobile-signature' />,
    },
    info: {
      bg: 'bg-cencert-validation-light border-cencert-validation',
      text: 'text-cencert-navy',
      icon: <Info className='w-5 h-5 text-cencert-navy' />,
    },
  }[variant]

  const baseClasses = `
    flex items-center gap-3 rounded-3xl 
    p-3 mb-4 border 
    ${variantClasses.bg} 
    ${variantClasses.text}
  `

  return (
    <div
      className={`${baseClasses} ${className}`}
      role='alert'
      aria-live={
        variant === 'error' || variant === 'warning' ? 'assertive' : 'polite'
      }
    >
      {variantClasses.icon}
      <div className='flex-1 text-body-sm font-medium'>
        {displayMessage || message}
      </div>
      {onDismiss && (
        <button
          onClick={onDismiss}
          aria-label={dismissLabel || t('components.alert.dismiss')}
          className='
            w-6 h-6 rounded-full 
            hover:bg-black hover:bg-opacity-10 
            flex items-center justify-center 
            transition-colors
          '
        >
          <X className='w-4 h-4' />
        </button>
      )}
    </div>
  )
}
